/** @format */


export const ProductStatus = {
  DRAFT: "DRAFT",
  PUBLISHED: "PUBLISHED",
} as const;

export type ProductStatus = (typeof ProductStatus)[keyof typeof ProductStatus];

export type Colours = {
  productId: string;
  colourId: string;
  assetId: string;
  colour: {
    id: string;
    name: string;
    hexValue: string;
    adminId: string;
    createdAt: string;
    updatedAt: string;
    colourId: string;
    imageUrls: string[] | [];
    quantity: number;
    weight: number;
  };
};


type productSize = {
  id: string;
  name: string;
  sizeId: string;
};

type productMaterial = {
  material: { id: string; name: string };
  materialId: string;
  additionalPrice: number;
  additionalCostPrice: number;
  colours: {
    images: never[];
    additionalPrice: number;
    additionalCostPrice: number;
    colour: {
      id: string;
      name: string;
      hexValue: string;
    };
    colourId: string;
    id: string;
    imageUrls: string[] | [];
    quantity: number;
    weight: number;
  }[];
};

export interface Product {
  adminId: string;
  merchantId?: string;
  category: {
    id: string;
    name: string;
  };
  categoryId: string;
  createdAt: string;
  deletedAt: string | null;
  description: string;
  id: string;
  name: string;
  quantity: number;
  sellingPrice: number;
  basePrice: number;
  baseCostPrice: number;
  productionTimeInDays: number;
  status: ProductStatus;
  updatedAt: string;
  sizeGuide: {
    id: string;
  };
  sizes: {
    size: productSize;
    additionalPrice?: number;
    materials: productMaterial[];
    additionalCostPrice?: number;
  }[];
  image: string | null;
  material: null;
  data:
    | {
        basePrice: number;
        categoryId: string;
        description: string;
      }
    | undefined;
}

export interface ProductsResponse {
  data: Product[];
  links: {
    first: string;
    last: string;
  };
  meta: {
    endCursor: string;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
    lastCursor: string;
    startCursor: string;
    totalCount: number;
  };
}

export interface ProductsParams {
  "page[size]"?: number;
  "page[after]"?: string;
  "page[before]"?: string;
  sort?: string[];
  "filter[name]"?: string;
  "filter[category.name]"?: string;
}

export interface Variant {
  price: number;
  costPrice: number;
  size: string;
  materialId: string;
  colourId: string;
  sizeGuideId: string;
  quantity: number;
  imageUrl?: string;
  stockUpTrigger?: number;
}
export interface CreateProductData {
  name: string;
  description: string;
  categoryId: string;
  sizeGuideId: string;
  basePrice: number;
  baseCostPrice: number;
  productionTimeInDays: number;
  status?: ProductStatus;
  sizes: {
    sizeId: string;
    additionalPrice: number;
    additionalCostPrice: number;
    materials: {
      materialId: string;
      additionalPrice: number;
      additionalCostPrice: number;
      colours: {
        colourId: string;
        additionalPrice: number;
        additionalCostPrice: number;
        quantity: number;
        weight: number;
        imageUrls: string[];
        stockUpTrigger: number;
      }[];
    }[];
  }[];
}

export interface ProductFormData {
  name: string;
  category: string;
  details: string;
  price: string;
  productionTimeInDays: string;
  materialType: string;
  availability: string;
  publish: boolean;
}

export interface ProductVariant {
  price: number;
  costPrice: number;
  size: string;
  materialId: string;
  colourId: string;
  sizeGuideId: string;
  quantity: number;
  imageUrl?: string;
  stockUpTrigger: number;
}

export interface ProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  product?: Product | null;
}

export interface ProductDetailsModalProps extends ProductModalProps {
  onEdit?: (id: string) => void;
  onPublish?: (id: string) => void;
  onDelete?: (id: string) => void;
  isPublishing?: boolean;
  isDeleting?: boolean;
}

export type AttributeDataType = 'ENUM' | 'COLOR' | 'STRING' | 'NUMBER';

export interface AttributeSchema {
  id: string;
  solutionId: string;
  tenantId: string | null;
  key: string;
  label: string;
  dataType: AttributeDataType;
  allowedValues: string[];
  isRequired: boolean;
  sortOrder: number;
}

export const SOLUTION_IDS = ['rack-and-box', 'atelier', 'spice-market'] as const;
export type SolutionId = (typeof SOLUTION_IDS)[number];

export type VariantMode = 'HIERARCHICAL' | 'PARTIAL' | 'INDEPENDENT';

export interface PlatformVariantDraft {
  sku: string;
  price?: number;
  stock?: number;
  parentId?: string;
  attributes: Record<string, string>;
}

export interface PlatformCreateProductDto {
  name: string;
  slug: string;
  description?: string;
  basePrice: number;
  solutionId: string;
  merchantId: string;
  status: 'DRAFT' | 'PUBLISHED';
  variantMode: VariantMode;
  categoryId?: string;
}

export interface PlatformProduct {
  id: string;
  name: string;
  description: string | null;
  basePrice: number;
  solutionId: string;
  merchantId: string;
  status: 'DRAFT' | 'PUBLISHED';
  categoryId: string | null;
  createdAt: string;
  updatedAt: string;
}
